import React from 'react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { Box, IconButton, useMediaQuery } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { TypographyTitleh6 } from 'components/Typography';
import { useTheme } from '@mui/system';

interface LayoutWithTopBarProps {
    children: React.ReactNode;
    title?: string;
    onBack: () => void;
    onShare: () => void;
    right?: React.ReactNode;
    showShareButton?: boolean;
    mobileGlassUi?: boolean;
    scrollHeaderWithContent?: boolean;
}

const TopBarWithShareIconLayout = ({
    title = '',
    children,
    onBack,
    onShare,
    showShareButton = true,
    right = <></>,
    mobileGlassUi = false,
    scrollHeaderWithContent = false,
}: LayoutWithTopBarProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const showGlassMobile = mobileGlassUi && isMobile;

    const glassSurfaceSx = showGlassMobile
        ? {
              background: `linear-gradient(135deg, ${theme.palette.background.paper}CC 0%, ${theme.palette.background.paper}99 100%)`,
              backdropFilter: 'blur(14px) saturate(160%)',
              WebkitBackdropFilter: 'blur(14px) saturate(160%)',
              border: `1px solid ${theme.palette.divider}33`,
              boxShadow: `0 6px 20px ${theme.palette.common.black}1A`,
          }
        : {};

    return (
        <Box
            sx={{
                flex: 1,
                height: scrollHeaderWithContent ? 'auto' : '100%',
                background: theme.palette.backgroundColor.action,
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    height: '65px',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: showGlassMobile ? theme.spacing(2, 3) : theme.spacing(8),
                    borderBottom: `1px solid ${theme.palette.primary.main}`,
                    background: theme.palette.backgroundColor.topBar,
                }}
            >
                <IconButton
                    sx={{
                        padding: showGlassMobile ? theme.spacing(0.5, 2) : theme.spacing(0, 4),
                        color: theme.palette.text.secondary,
                        borderRadius: showGlassMobile ? '12px' : undefined,
                        ...glassSurfaceSx,
                    }}
                    onClick={onBack}
                    size="large"
                >
                    <ArrowBackIosIcon color="inherit" />
                </IconButton>
                <Box
                    sx={{
                        px: showGlassMobile ? { xs: 2, sm: 3 } : 0,
                        py: showGlassMobile ? { xs: 0.75, sm: 1 } : 0,
                        borderRadius: showGlassMobile ? '14px' : undefined,
                        minWidth: 0,
                        maxWidth: showGlassMobile ? '70%' : '100%',
                        ...glassSurfaceSx,
                    }}
                >
                    <TypographyTitleh6
                        sx={{
                            color: theme.palette.text.primary,
                            maxWidth: '100%',
                            overflow: 'hidden',
                            display: 'block',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {title}
                    </TypographyTitleh6>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: showGlassMobile ? '12px' : undefined,
                        ...glassSurfaceSx,
                    }}
                >
                    {right}
                    {showShareButton && (
                        <IconButton
                            sx={{
                                padding: showGlassMobile
                                    ? theme.spacing(0.5, 2)
                                    : theme.spacing(0, 4),
                                color: theme.palette.text.secondary,
                            }}
                            onClick={onShare}
                            size="large"
                        >
                            <QrCode2Icon color="inherit" />
                            <ShareIcon color="inherit" />
                        </IconButton>
                    )}
                </Box>
            </Box>
            <Box
                sx={{
                    flex: 1,
                    height: scrollHeaderWithContent ? 'auto' : 'calc(100% - 65px)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

export default TopBarWithShareIconLayout;
